// Shared pieces for the P6-B admin screens: error copy by code, shipment-state badges (tone + text,
// never colour alone), test IDs and the "open printable document" helper.
import { i18n, t } from "@ady/i18n";
import { Badge, type Tone } from "@ady/ui";
import { ApiError } from "../api/client";
import { errorMessage } from "../api/errors";
import { isStepUpCancelled } from "../auth/StepUp";
import type { ShipmentState } from "./api";

export const FTID = {
  dispatchPage: "fulfilment-dispatch",
  book: "fulfilment-book",
  pickList: "fulfilment-pick-list",
  shipments: "fulfilment-shipments",
  couriersPage: "fulfilment-couriers",
  courierCard: "fulfilment-courier-card",
  returnsPage: "fulfilment-returns",
  invoicesPage: "fulfilment-invoices",
  codPage: "fulfilment-cod-checks",
  protectionPage: "fulfilment-protection",
} as const;

const CODE_KEYS: Record<string, string> = {
  "shipping.no_courier": "fulfilment.errors.noCourier",
  "shipping.no_courier_for_area": "fulfilment.errors.noCourierForArea",
  "shipping.order_not_ready": "fulfilment.errors.notReady",
  "shipping.address_missing": "fulfilment.errors.addressMissing",
  "shipping.cancel_too_late": "fulfilment.errors.cancelTooLate",
  "shipping.cancel_at_courier": "fulfilment.errors.cancelAtCourier",
  "shipping.courier_refused": "fulfilment.errors.courierRefused",
  "shipping.courier_unreachable": "fulfilment.errors.courierUnreachable",
  "shipping.courier_inactive": "fulfilment.errors.courierInactive",
  "shipping.portal_password_refused": "fulfilment.errors.portalPassword",
  "order.damaged_not_restocked": "fulfilment.errors.damagedNotRestocked",
  "payment.refund_exceeds_line": "fulfilment.errors.refundTooHigh",
  "order.return_qty_exceeded": "fulfilment.errors.returnQty",
  "order.not_returnable": "fulfilment.errors.notReturnable",
  "order.not_paid": "fulfilment.errors.notPaid",
};

/** Copy for an error by its code (D-013: never the server's message); null when step-up was closed. */
export function fulfilmentError(err: unknown): string | null {
  if (isStepUpCancelled(err)) return null;
  if (err instanceof ApiError) {
    const key = CODE_KEYS[err.code];
    if (key) return i18n.tDynamic(key, "fulfilment.errors.generic");
    if (err.fields?.pickup_branch === "unknown_branch") return t("fulfilment.errors.unknownBranch");
    if (err.code === "dependency.activation_required") {
      const dep = String(err.details?.dependency ?? "");
      if (dep === "credential_vault") return t("fulfilment.errors.vaultPending");
      if (dep === "refund_service") return t("fulfilment.errors.refundsPending");
      if (dep.startsWith("courier:")) return t("fulfilment.errors.specRequired");
    }
  }
  return errorMessage(err);
}

const STATE_TONE: Record<ShipmentState, Tone> = {
  draft: "neutral",
  booked: "action",
  picked_up: "transit",
  in_transit: "transit",
  out_for_delivery: "transit",
  delivered: "success",
  failed_attempt: "warning",
  rto: "danger",
  cancelled: "neutral",
};

/** The courier's display name (the simulated courier says so). */
export function courierName(courier: string): string {
  if (courier === "stub") return t("fulfilment.dispatch.testCourier");
  return i18n.tDynamic(
    `fulfilment.couriers.names.${courier}`,
    "fulfilment.couriers.catalogueTitle",
  );
}

export function ShipmentBadge({ state }: { state: ShipmentState }) {
  return <Badge tone={STATE_TONE[state]}>{t(`fulfilment.shipmentState.${state}`)}</Badge>;
}

export function openDocument(url: string): void {
  window.open(url, "_blank", "noopener");
}
